"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  Crown,
  Star,
  Sparkles,
  Heart,
  Lock,
} from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type AchievementsExperienceData,
  type AchievementIcon as AchievementIconKeyExternal,
  type AchievementRarity as AchievementRarityKeyExternal,
} from "@/data/experienceDataSchema";

export type AchievementsVariant = "default" | "spotify";

interface AchievementsPalette {
  accent: string;
  accentRgb: string;
}

interface AchievementsSectionProps {
  gift: Partial<Gift>;
  variant: AchievementsVariant;
  palette?: AchievementsPalette;
  className?: string;
}

type Rarity = "Comum" | "Raro" | "Épico" | "Lendário";

type AchievementIconKey = "trophy" | "heart" | "star" | "crown";

interface DerivedAchievement {
  id: string;
  title: string;
  description: string;
  rarity: Rarity;
  threshold: number; // dias mínimos para desbloquear
  unlocked: boolean;
  icon: AchievementIconKey;
}

const ACHIEVEMENT_TEMPLATES: ReadonlyArray<
  Omit<DerivedAchievement, "unlocked">
> = [
  {
    id: "first-chapter",
    title: "Primeiro Capítulo",
    description: "A história de vocês começou e virou memória.",
    rarity: "Comum",
    threshold: 0,
    icon: "heart",
  },
  {
    id: "hundred-days",
    title: "100 Dias Juntos",
    description: "Cem dias construindo momentos especiais.",
    rarity: "Raro",
    threshold: 100,
    icon: "star",
  },
  {
    id: "one-year",
    title: "365 Dias de História",
    description: "Um ano inteiro de lembranças, carinho e parceria.",
    rarity: "Épico",
    threshold: 365,
    icon: "trophy",
  },
  {
    id: "legendary-love",
    title: "Amor Lendário",
    description: "Uma história que continua desbloqueando novos níveis.",
    rarity: "Lendário",
    threshold: 1000,
    icon: "crown",
  },
];

function parseCoupleStartDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function computeDaysTogether(start: Date, now: Date): number {
  const diffMs = Math.max(0, now.getTime() - start.getTime());
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function deriveAchievements(daysTogether: number | null): DerivedAchievement[] {
  return ACHIEVEMENT_TEMPLATES.map((tpl) => ({
    ...tpl,
    unlocked: daysTogether === null ? false : daysTogether >= tpl.threshold,
  }));
}

function formatThreshold(threshold: number): string {
  if (threshold <= 0) return "Desbloqueada";
  return `${threshold.toLocaleString("pt-BR")} dias`;
}

function pickIcon(key: AchievementIconKey, className: string) {
  switch (key) {
    case "heart":
      return <Heart className={className} />;
    case "star":
      return <Star className={className} />;
    case "trophy":
      return <Trophy className={className} />;
    case "crown":
      return <Crown className={className} />;
    default:
      return <Trophy className={className} />;
  }
}

// Mapeia o ícone do schema (heart | trophy | star | gift) para a chave usada
// internamente pelo componente (heart | trophy | star | crown).
function mapSchemaIconToLocal(
  icon?: AchievementIconKeyExternal
): AchievementIconKey {
  switch (icon) {
    case "heart":
      return "heart";
    case "trophy":
      return "trophy";
    case "star":
      return "star";
    case "gift":
      // O ícone "gift" do schema vira "crown" visual; mantemos a simbologia
      // de algo raro/especial.
      return "crown";
    default:
      return "trophy";
  }
}

const RARITY_LABEL: Record<AchievementRarityKeyExternal, Rarity> = {
  common: "Comum",
  rare: "Raro",
  epic: "Épico",
  legendary: "Lendário",
};

// Conquistas personalizadas pelo usuário sobrescrevem completamente as
// derivadas (que são baseadas no tempo de casal). Quando há custom, o usuário
// controla integralmente a lista exibida.
function buildCustomAchievements(
  items: AchievementsExperienceData["achievements"]
): DerivedAchievement[] | null {
  if (!Array.isArray(items) || items.length === 0) return null;
  const result: DerivedAchievement[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i] ?? {};
    const title = item.title?.trim();
    const description = item.description?.trim();
    if (!title && !description) continue;
    const rarityKey = (item.rarity ?? "common") as AchievementRarityKeyExternal;
    result.push({
      id: `custom-${i}`,
      title: title || "Conquista",
      description: description || "Um marco especial dessa história.",
      rarity: RARITY_LABEL[rarityKey] ?? "Comum",
      threshold: 0,
      unlocked: item.unlocked ?? true,
      icon: mapSchemaIconToLocal(item.icon),
    });
  }
  return result.length > 0 ? result : null;
}

export function AchievementsSection({
  gift,
  variant,
  palette,
  className,
}: AchievementsSectionProps) {
  const startDate = useMemo(
    () => parseCoupleStartDate(gift.coupleStartDate),
    [gift.coupleStartDate]
  );

  // Cálculo estático (sem timer) — o objetivo aqui é apresentar marcos, não
  // contagem ao vivo. Evita rerender constante e mantém o componente simples.
  const daysTogether = useMemo(() => {
    if (!startDate) return null;
    return computeDaysTogether(startDate, new Date());
  }, [startDate]);

  const customData = useMemo(
    () =>
      getExperienceData<AchievementsExperienceData>(gift, "achievements"),
    [gift]
  );

  const achievements = useMemo(() => {
    const custom = buildCustomAchievements(customData?.achievements);
    if (custom) return custom;
    return deriveAchievements(daysTogether);
  }, [customData, daysTogether]);

  const highlighted = useMemo(() => {
    const unlocked = achievements.filter((a) => a.unlocked);
    if (unlocked.length === 0) return achievements[0];
    return unlocked[unlocked.length - 1];
  }, [achievements]);

  const rest = useMemo(
    () => achievements.filter((a) => a.id !== highlighted.id),
    [achievements, highlighted.id]
  );

  const customTitle = customData?.title?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        achievements={achievements}
        highlighted={highlighted}
        rest={rest}
        daysTogether={daysTogether}
        className={className}
        title={customTitle}
      />
    );
  }

  return (
    <DefaultVariant
      achievements={achievements}
      highlighted={highlighted}
      rest={rest}
      daysTogether={daysTogether}
      className={className}
      title={customTitle}
    />
  );
}

// ─── Variante default — clara, romântica, premium ───────────────────────────

interface VariantInnerProps {
  achievements: DerivedAchievement[];
  highlighted: DerivedAchievement;
  rest: DerivedAchievement[];
  daysTogether: number | null;
  className?: string;
  title?: string;
}

function DefaultVariant({
  highlighted,
  rest,
  daysTogether,
  className,
  title,
}: VariantInnerProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "achievements-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DecorativeGlow variant="default" />

      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Trophy
              className="h-3 w-3 fill-[#F59E0B] text-[#F59E0B]"
              aria-hidden
            />
            Conquistas do casal
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Marcos que vocês desbloquearam"}
          </h2>
          <p className="max-w-md text-sm text-[#1F2937]/70 leading-relaxed">
            Cada momento importante vira uma conquista. Estas são as medalhas
            que a história de vocês conquistou até aqui.
          </p>
        </header>

        <HighlightCardLight
          achievement={highlighted}
          daysTogether={daysTogether}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((a) => (
            <AchievementCardLight key={a.id} achievement={a} />
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function HighlightCardLight({
  achievement,
  daysTogether,
}: {
  achievement: DerivedAchievement;
  daysTogether: number | null;
}) {
  const rarityStyle = LIGHT_RARITY[achievement.rarity];
  return (
    <div
      className={cn(
        "achievements-highlight-card relative overflow-hidden rounded-2xl border bg-white/85 px-5 py-6 sm:px-7 sm:py-7 shadow-md backdrop-blur",
        achievement.unlocked ? "" : "opacity-80"
      )}
      style={{ borderColor: rarityStyle.borderLight }}
      aria-label={`Conquista em destaque: ${achievement.title}`}
    >
      {achievement.unlocked ? (
        <span className="achievements-shimmer pointer-events-none absolute inset-0" />
      ) : null}

      <div className="relative z-10 flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:text-left">
        <div
          className="relative flex h-20 w-20 items-center justify-center rounded-full shadow-inner"
          style={{
            background: `linear-gradient(135deg, ${rarityStyle.iconBgFrom} 0%, ${rarityStyle.iconBgTo} 100%)`,
            boxShadow: achievement.unlocked
              ? `0 0 28px ${rarityStyle.glow}`
              : "inset 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          <span
            className="absolute inset-1 rounded-full"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.5) 100%)",
            }}
            aria-hidden
          />
          {pickIcon(
            achievement.icon,
            "relative z-10 h-9 w-9 drop-shadow-sm"
          )}
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            <RarityBadgeLight rarity={achievement.rarity} />
            {achievement.unlocked ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#047857]">
                <Sparkles className="h-3 w-3" aria-hidden />
                Desbloqueada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#1F2937]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1F2937]/70">
                <Lock className="h-3 w-3" aria-hidden />
                {formatThreshold(achievement.threshold)}
              </span>
            )}
          </div>

          <h3 className="text-xl font-bold leading-tight text-[#1F2937] sm:text-2xl">
            {achievement.title}
          </h3>
          <p className="text-sm leading-relaxed text-[#1F2937]/75">
            {achievement.description}
          </p>

          {daysTogether !== null ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#881337]/70">
              {daysTogether.toLocaleString("pt-BR")} dias juntos
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AchievementCardLight({
  achievement,
}: {
  achievement: DerivedAchievement;
}) {
  const rarityStyle = LIGHT_RARITY[achievement.rarity];
  return (
    <div
      className={cn(
        "achievements-card relative flex flex-col gap-3 rounded-2xl border bg-white/75 p-4 shadow-sm backdrop-blur transition-transform hover:-translate-y-0.5",
        achievement.unlocked ? "" : "opacity-70"
      )}
      style={{ borderColor: rarityStyle.borderLight }}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-xl shadow-inner"
          style={{
            background: `linear-gradient(135deg, ${rarityStyle.iconBgFrom} 0%, ${rarityStyle.iconBgTo} 100%)`,
            boxShadow: achievement.unlocked
              ? `0 0 18px ${rarityStyle.glow}`
              : "inset 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          {pickIcon(achievement.icon, "h-5 w-5 drop-shadow-sm")}
        </div>
        <RarityBadgeLight rarity={achievement.rarity} />
      </div>

      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-bold leading-tight text-[#1F2937]">
          {achievement.title}
        </h4>
        <p className="text-xs leading-relaxed text-[#1F2937]/70">
          {achievement.description}
        </p>
      </div>

      {achievement.unlocked ? (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#047857]">
          <Sparkles className="h-3 w-3" aria-hidden />
          Desbloqueada
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#1F2937]/60">
          <Lock className="h-3 w-3" aria-hidden />
          {formatThreshold(achievement.threshold)}
        </span>
      )}
    </div>
  );
}

function RarityBadgeLight({ rarity }: { rarity: Rarity }) {
  const style = LIGHT_RARITY[rarity];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{
        background: style.badgeBg,
        color: style.badgeText,
        border: `1px solid ${style.borderLight}`,
      }}
    >
      {rarity}
    </span>
  );
}

// ─── Variante Spotify — escura, imersiva, premium ───────────────────────────

interface SpotifyVariantProps extends VariantInnerProps {
  palette: AchievementsPalette;
}

function SpotifyVariant({
  palette,
  highlighted,
  rest,
  daysTogether,
  className,
  title,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "achievements-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(245,158,11,0.08) 40%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <DecorativeGlow variant="spotify" accent={palette.accent} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: "#F59E0B" }}>
            <Trophy className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Conquistas do casal
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Marcos que vocês desbloquearam"}
          </h3>
          <p className="text-[11px] text-white/55 leading-relaxed">
            Cada momento da história vira uma medalha. Veja as que já fazem
            parte da sua jornada.
          </p>
        </div>

        <HighlightCardDark
          achievement={highlighted}
          daysTogether={daysTogether}
        />

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {rest.map((a) => (
            <AchievementCardDark key={a.id} achievement={a} />
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function HighlightCardDark({
  achievement,
  daysTogether,
}: {
  achievement: DerivedAchievement;
  daysTogether: number | null;
}) {
  const rarityStyle = DARK_RARITY[achievement.rarity];
  return (
    <div
      className={cn(
        "achievements-highlight-card relative overflow-hidden rounded-xl p-4 sm:p-5 backdrop-blur",
        achievement.unlocked ? "" : "opacity-80"
      )}
      style={{
        background: rarityStyle.cardBg,
        border: `1px solid ${rarityStyle.borderDark}`,
      }}
      aria-label={`Conquista em destaque: ${achievement.title}`}
    >
      {achievement.unlocked ? (
        <span className="achievements-shimmer pointer-events-none absolute inset-0" />
      ) : null}

      <div className="relative z-10 flex items-center gap-4">
        <div
          className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full"
          style={{
            background: `linear-gradient(135deg, ${rarityStyle.iconBgFrom} 0%, ${rarityStyle.iconBgTo} 100%)`,
            boxShadow: achievement.unlocked
              ? `0 0 22px ${rarityStyle.glow}`
              : "inset 0 0 0 1px rgba(255,255,255,0.05)",
          }}
        >
          <span
            className="absolute inset-1 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25) 0%, rgba(0,0,0,0.35) 70%)",
            }}
            aria-hidden
          />
          {pickIcon(achievement.icon, "relative z-10 h-6 w-6 text-white")}
        </div>

        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <RarityBadgeDark rarity={achievement.rarity} />
            {achievement.unlocked ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
                <Sparkles className="h-2.5 w-2.5" aria-hidden />
                Desbloqueada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/65">
                <Lock className="h-2.5 w-2.5" aria-hidden />
                {formatThreshold(achievement.threshold)}
              </span>
            )}
          </div>

          <h4 className="text-base font-bold leading-tight text-white">
            {achievement.title}
          </h4>
          <p className="text-[11px] leading-relaxed text-white/70">
            {achievement.description}
          </p>

          {daysTogether !== null ? (
            <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-white/45">
              {daysTogether.toLocaleString("pt-BR")} dias juntos
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AchievementCardDark({
  achievement,
}: {
  achievement: DerivedAchievement;
}) {
  const rarityStyle = DARK_RARITY[achievement.rarity];
  return (
    <div
      className={cn(
        "achievements-card relative flex flex-col gap-2 rounded-xl p-3 backdrop-blur",
        achievement.unlocked ? "" : "opacity-65"
      )}
      style={{
        background: rarityStyle.cardBg,
        border: `1px solid ${rarityStyle.borderDark}`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${rarityStyle.iconBgFrom} 0%, ${rarityStyle.iconBgTo} 100%)`,
            boxShadow: achievement.unlocked
              ? `0 0 14px ${rarityStyle.glow}`
              : "inset 0 0 0 1px rgba(255,255,255,0.05)",
          }}
        >
          {pickIcon(achievement.icon, "h-4 w-4 text-white")}
        </div>
        <RarityBadgeDark rarity={achievement.rarity} />
      </div>

      <div className="flex flex-col gap-0.5">
        <h5 className="text-[12px] font-bold leading-tight text-white">
          {achievement.title}
        </h5>
        <p className="text-[10px] leading-relaxed text-white/60">
          {achievement.description}
        </p>
      </div>

      {achievement.unlocked ? (
        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
          <Sparkles className="h-2.5 w-2.5" aria-hidden />
          Desbloqueada
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white/50">
          <Lock className="h-2.5 w-2.5" aria-hidden />
          {formatThreshold(achievement.threshold)}
        </span>
      )}
    </div>
  );
}

function RarityBadgeDark({ rarity }: { rarity: Rarity }) {
  const style = DARK_RARITY[rarity];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
      style={{
        background: style.badgeBg,
        color: style.badgeText,
        border: `1px solid ${style.borderDark}`,
      }}
    >
      {rarity}
    </span>
  );
}

// ─── Decoração e estilos compartilhados ─────────────────────────────────────

function DecorativeGlow({
  variant,
  accent,
}: {
  variant: AchievementsVariant;
  accent?: string;
}) {
  if (variant === "spotify") {
    return (
      <>
        <Trophy
          className="absolute -top-3 -right-3 h-20 w-20 opacity-10"
          style={{ color: accent ?? "#F59E0B" }}
          fill="currentColor"
          aria-hidden
        />
        <Sparkles
          className="absolute bottom-4 -left-2 h-10 w-10 opacity-15"
          style={{ color: "#F59E0B" }}
          aria-hidden
        />
        <style>{SHIMMER_STYLE}</style>
      </>
    );
  }
  return (
    <>
      <Trophy
        className="absolute -top-4 -right-4 h-24 w-24 text-[#F59E0B]/20"
        fill="currentColor"
        aria-hidden
      />
      <Sparkles
        className="absolute top-6 left-5 h-4 w-4 text-[#E11D48]/40"
        aria-hidden
      />
      <Heart
        className="absolute -bottom-3 -left-3 h-16 w-16 text-[#E11D48]/15"
        fill="currentColor"
        aria-hidden
      />
      <style>{SHIMMER_STYLE}</style>
    </>
  );
}

// Shimmer/glow leve aplicado por classes locais — evita CSS global e mantém o
// efeito isolado a este componente. Não usa Tailwind keyframes para não exigir
// configuração no projeto.
const SHIMMER_STYLE = `
.achievements-shimmer {
  background: linear-gradient(
    115deg,
    transparent 0%,
    transparent 35%,
    rgba(255, 232, 168, 0.35) 50%,
    transparent 65%,
    transparent 100%
  );
  background-size: 220% 100%;
  background-position: 220% 0;
  animation: achievementsShimmerMove 4.8s ease-in-out infinite;
  mix-blend-mode: screen;
}
@keyframes achievementsShimmerMove {
  0% { background-position: 220% 0; }
  60% { background-position: -120% 0; }
  100% { background-position: -120% 0; }
}
`;

// ─── Paletas por raridade ───────────────────────────────────────────────────

interface RarityLightStyle {
  badgeBg: string;
  badgeText: string;
  borderLight: string;
  iconBgFrom: string;
  iconBgTo: string;
  glow: string;
}

interface RarityDarkStyle {
  badgeBg: string;
  badgeText: string;
  borderDark: string;
  cardBg: string;
  iconBgFrom: string;
  iconBgTo: string;
  glow: string;
}

const LIGHT_RARITY: Record<Rarity, RarityLightStyle> = {
  Comum: {
    badgeBg: "rgba(255,228,230,0.85)",
    badgeText: "#9F1239",
    borderLight: "rgba(225,29,72,0.22)",
    iconBgFrom: "#FECDD3",
    iconBgTo: "#FDA4AF",
    glow: "rgba(225,29,72,0.25)",
  },
  Raro: {
    badgeBg: "rgba(219,234,254,0.85)",
    badgeText: "#1D4ED8",
    borderLight: "rgba(59,130,246,0.28)",
    iconBgFrom: "#BFDBFE",
    iconBgTo: "#93C5FD",
    glow: "rgba(59,130,246,0.3)",
  },
  Épico: {
    badgeBg: "rgba(237,233,254,0.9)",
    badgeText: "#6D28D9",
    borderLight: "rgba(139,92,246,0.32)",
    iconBgFrom: "#DDD6FE",
    iconBgTo: "#C4B5FD",
    glow: "rgba(139,92,246,0.35)",
  },
  Lendário: {
    badgeBg: "rgba(254,243,199,0.9)",
    badgeText: "#92400E",
    borderLight: "rgba(245,158,11,0.4)",
    iconBgFrom: "#FDE68A",
    iconBgTo: "#F59E0B",
    glow: "rgba(245,158,11,0.45)",
  },
};

const DARK_RARITY: Record<Rarity, RarityDarkStyle> = {
  Comum: {
    badgeBg: "rgba(244,114,182,0.18)",
    badgeText: "#FBCFE8",
    borderDark: "rgba(244,114,182,0.28)",
    cardBg: "rgba(244,114,182,0.06)",
    iconBgFrom: "#9D174D",
    iconBgTo: "#BE185D",
    glow: "rgba(244,114,182,0.3)",
  },
  Raro: {
    badgeBg: "rgba(96,165,250,0.18)",
    badgeText: "#BFDBFE",
    borderDark: "rgba(96,165,250,0.3)",
    cardBg: "rgba(96,165,250,0.06)",
    iconBgFrom: "#1D4ED8",
    iconBgTo: "#3B82F6",
    glow: "rgba(96,165,250,0.35)",
  },
  Épico: {
    badgeBg: "rgba(167,139,250,0.2)",
    badgeText: "#DDD6FE",
    borderDark: "rgba(167,139,250,0.32)",
    cardBg: "rgba(167,139,250,0.06)",
    iconBgFrom: "#6D28D9",
    iconBgTo: "#8B5CF6",
    glow: "rgba(167,139,250,0.4)",
  },
  Lendário: {
    badgeBg: "rgba(250,204,21,0.18)",
    badgeText: "#FDE68A",
    borderDark: "rgba(245,158,11,0.4)",
    cardBg: "rgba(245,158,11,0.08)",
    iconBgFrom: "#B45309",
    iconBgTo: "#F59E0B",
    glow: "rgba(245,158,11,0.5)",
  },
};
